import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Papa from "papaparse";
import {
  Sparkles,
  ArrowLeft,
  Upload,
  Users,
  Send,
  Play,
  Pause,
  Trash2,
  Clock,
  Reply,
  CheckCircle2,
  FileSpreadsheet,
  Plus,
  Wand2,
  Edit,
  Eye,
  ChevronDown,
  ChevronUp,
  Mail,
  AlertCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import Sidebar from "../components/Sidebar";
import AccountMultiSelect from "../components/AccountMultiSelect";
import { TimezoneSelect } from "../components/TimezoneSelect";
import { api } from "../App";
import { toast } from "sonner";

const DAYS = [
  { v: 0, l: "Mon" },
  { v: 1, l: "Tue" },
  { v: 2, l: "Wed" },
  { v: 3, l: "Thu" },
  { v: 4, l: "Fri" },
  { v: 5, l: "Sat" },
  { v: 6, l: "Sun" },
];

const TONES = [
  "Professional and friendly",
  "Warm and casual",
  "Direct and concise",
  "Enthusiastic and energetic",
  "Formal and corporate",
  "Consultative and helpful",
];

const statusBadge = (status) =>
  status === "running"
    ? "bg-emerald-100 text-emerald-700"
    : status === "paused"
    ? "bg-amber-100 text-amber-700"
    : status === "completed"
    ? "bg-blue-100 text-blue-700"
    : "bg-slate-100 text-slate-600";

export default function AISalesOutreach({ user, setUser }) {
  const navigate = useNavigate();
  const [view, setView] = useState("list");
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editContactCount, setEditContactCount] = useState(0);
  const [logsCampaign, setLogsCampaign] = useState(null);
  const [logEntries, setLogEntries] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);
  const fileInputRef = useRef(null);

  // Create form state
  const emptyForm = {
    name: "",
    from_name: "",
    account_ids: [],
    product_service: "",
    offer: "",
    links: "",
    cta: "",
    tone: TONES[0],
    instructions: "",
    signature: "",
    num_followups: 2,
    followup_delay_days: 3,
    timezone: "UTC",
    sending_days: [0, 1, 2, 3, 4],
    start_time: "09:00",
    end_time: "18:00",
    start_date: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [prospects, setProspects] = useState([]);
  const [columns, setColumns] = useState([]);
  const [fileName, setFileName] = useState("");

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchData = useCallback(async () => {
    try {
      const [campRes, accRes] = await Promise.all([
        api.get("/ai-outreach/campaigns"),
        api.get("/accounts"),
      ]);
      setCampaigns(campRes.data || []);
      setAccounts(accRes.data?.accounts || accRes.data || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("AI Sales Outreach is not enabled for your account.");
        navigate("/dashboard");
      } else {
        toast.error("Failed to load AI outreach data");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const fields = results.meta.fields || [];
        const emailKey = fields.find((f) => /e-?mail/i.test(f));
        if (!emailKey) {
          toast.error("No 'email' column found in the file");
          setProspects([]);
          setColumns([]);
          return;
        }
        const rows = (results.data || [])
          .map((r) => {
            const email = (r[emailKey] || "").trim().toLowerCase();
            if (!email || !email.includes("@")) return null;
            const obj = { email };
            fields.forEach((f) => {
              if (f === emailKey) return;
              const key = f.trim().toLowerCase().replace(/\s+/g, "_");
              if (key && r[f]) obj[key] = String(r[f]).trim();
            });
            return obj;
          })
          .filter(Boolean);
        if (rows.length === 0) {
          toast.error("No valid prospects with email addresses found");
        } else {
          toast.success(`${rows.length} prospects loaded`);
        }
        setProspects(rows);
        setColumns(Object.keys(rows[0] || { email: "" }));
      },
      error: () => toast.error("Failed to parse the CSV file"),
    });
  };

  const toggleDay = (v) =>
    setForm((f) => ({
      ...f,
      sending_days: f.sending_days.includes(v)
        ? f.sending_days.filter((d) => d !== v)
        : [...f.sending_days, v].sort((a, b) => a - b),
    }));

  const canSubmit =
    form.name.trim() &&
    form.product_service.trim() &&
    form.account_ids.length > 0 &&
    (editId ? true : prospects.length > 0);

  const startCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setProspects([]);
    setColumns([]);
    setFileName("");
    setView("create");
  };

  const startEdit = async (c) => {
    try {
      const res = await api.get(`/ai-outreach/campaigns/${c.drip_id}`);
      const camp = res.data;
      const brief = camp.ai_brief || {};
      const sched = camp.schedule || {};
      const followups = Math.max(0, (camp.steps?.length || 1) - 1);
      const delay = camp.steps?.[1]?.delay_days || 3;
      setForm({
        name: camp.name || "",
        from_name: camp.from_name || "",
        account_ids: camp.account_ids || [],
        product_service: brief.product_service || "",
        offer: brief.offer || "",
        links: brief.links || "",
        cta: brief.cta || "",
        tone: brief.tone || TONES[0],
        instructions: brief.instructions || "",
        signature: brief.signature || "",
        num_followups: followups,
        followup_delay_days: delay,
        timezone: sched.timezone || "UTC",
        sending_days: sched.sending_days || [0, 1, 2, 3, 4],
        start_time: sched.start_time || "09:00",
        end_time: sched.end_time || "18:00",
        start_date: sched.start_date || "",
      });
      setEditContactCount(camp.stats?.total_contacts || 0);
      setEditId(c.drip_id);
      setView("create");
    } catch (error) {
      toast.error("Failed to load campaign for editing");
    }
  };

  const openLogs = async (c) => {
    setLogsCampaign(c);
    setView("logs");
    setLogsLoading(true);
    setExpandedLog(null);
    try {
      const res = await api.get(`/ai-outreach/campaigns/${c.drip_id}/logs`);
      setLogEntries(res.data.entries || []);
    } catch (error) {
      toast.error("Failed to load logs");
    } finally {
      setLogsLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!canSubmit) {
      toast.error("Please complete campaign name, brief and accounts" + (editId ? "" : " and prospect upload"));
      return;
    }
    setSubmitting(true);
    try {
      const briefPayload = {
        product_service: form.product_service,
        offer: form.offer,
        links: form.links,
        cta: form.cta,
        tone: form.tone,
        instructions: form.instructions,
        signature: form.signature,
      };
      const schedulePayload = {
        timezone: form.timezone,
        sending_days: form.sending_days,
        start_time: form.start_time,
        end_time: form.end_time,
        start_date: form.start_date || null,
      };
      if (editId) {
        await api.put(`/ai-outreach/campaigns/${editId}`, {
          name: form.name,
          from_name: form.from_name,
          account_ids: form.account_ids,
          brief: briefPayload,
          num_followups: Number(form.num_followups) || 0,
          followup_delay_days: Number(form.followup_delay_days) || 1,
          schedule: schedulePayload,
        });
        toast.success("Campaign updated");
      } else {
        await api.post("/ai-outreach/campaigns", {
          name: form.name,
          from_name: form.from_name,
          account_ids: form.account_ids,
          brief: briefPayload,
          num_followups: Number(form.num_followups) || 0,
          followup_delay_days: Number(form.followup_delay_days) || 1,
          schedule: schedulePayload,
          prospects,
          auto_start: true,
        });
        toast.success("AI campaign launched — the AI is generating personalised emails");
      }
      setForm(emptyForm);
      setProspects([]);
      setColumns([]);
      setFileName("");
      setEditId(null);
      setView("list");
      setLoading(true);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save campaign");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePauseResume = async (c) => {
    try {
      const action = c.status === "running" ? "pause" : "resume";
      await api.post(`/ai-outreach/campaigns/${c.drip_id}/${action}`);
      toast.success(action === "pause" ? "Campaign paused" : "Campaign started");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/ai-outreach/campaigns/${deleteId}`);
      toast.success("Campaign deleted");
      setDeleteId(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete campaign");
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar user={user} setUser={setUser} />
        <div className="flex-1 flex items-center justify-center">
          <div className="w-10 h-10 border-3 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // ─────────────────────────── LOGS VIEW ───────────────────────────
  if (view === "logs" && logsCampaign) {
    const statusPill = (s) =>
      s === "sent"
        ? "bg-emerald-100 text-emerald-700"
        : s === "failed"
        ? "bg-red-100 text-red-700"
        : "bg-amber-100 text-amber-700";
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar user={user} setUser={setUser} />
        <main className="flex-1 p-6 lg:p-8 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setView("list")}
              className="text-slate-500 hover:text-slate-700 -ml-2 mb-4"
              data-testid="ai-logs-back-btn"
            >
              <ArrowLeft size={16} className="mr-1.5" />
              Back
            </Button>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Mail size={20} className="text-white" />
              </div>
              <div>
                <h1 className="font-heading font-extrabold text-2xl text-slate-900">
                  {logsCampaign.name} — Send Log
                </h1>
                <p className="text-slate-500 text-sm">
                  Every email sent, to whom, from which account, and the exact AI-written content
                </p>
              </div>
            </div>

            {logsLoading ? (
              <div className="flex justify-center py-20">
                <div className="w-8 h-8 border-3 border-violet-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : logEntries.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-lg p-14 text-center">
                <Mail size={26} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">
                  No emails sent yet. Once the AI starts sending, every message will appear here.
                </p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="ai-logs-list">
                {logEntries.map((e, idx) => {
                  const open = expandedLog === idx;
                  return (
                    <div
                      key={idx}
                      className="bg-white border border-slate-200 rounded-lg overflow-hidden"
                      data-testid={`ai-log-entry-${idx}`}
                    >
                      <button
                        onClick={() => setExpandedLog(open ? null : idx)}
                        className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors text-left"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2.5">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusPill(e.status)}`}>
                              {e.status}
                            </span>
                            <span className="font-medium text-slate-900 truncate">{e.recipient}</span>
                            {e.step != null && (
                              <span className="text-xs text-slate-400">
                                {e.step === 1 ? "Initial" : `Follow-up ${e.step - 1}`}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-slate-500">
                            <span>from {e.account || "—"}</span>
                            {e.sent_at && <span>{new Date(e.sent_at).toLocaleString()}</span>}
                            {e.subject && <span className="truncate max-w-[280px]">“{e.subject}”</span>}
                          </div>
                        </div>
                        <div className="ml-3 text-slate-400">
                          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </div>
                      </button>
                      {open && (
                        <div className="border-t border-slate-100 p-4 bg-slate-50/60">
                          {e.status === "sent" ? (
                            <>
                              <p className="text-sm font-semibold text-slate-900 mb-2">
                                Subject: {e.subject || "(threaded reply — no subject)"}
                              </p>
                              <div
                                className="prose prose-sm max-w-none text-slate-700 bg-white border border-slate-200 rounded-md p-4"
                                data-testid={`ai-log-body-${idx}`}
                                dangerouslySetInnerHTML={{ __html: e.body || "<p>(no content)</p>" }}
                              />
                            </>
                          ) : (
                            <div className="flex items-start gap-2 text-sm text-red-600">
                              <AlertCircle size={16} className="mt-0.5 shrink-0" />
                              <span>{e.error || `This email was ${e.status}.`}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }


  // ─────────────────────────── CREATE VIEW ───────────────────────────
  if (view === "create") {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <Sidebar user={user} setUser={setUser} />
        <main className="flex-1 p-6 lg:p-8 overflow-y-auto">
          <div className="max-w-3xl mx-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setEditId(null); setView("list"); }}
              className="text-slate-500 hover:text-slate-700 -ml-2 mb-4"
              data-testid="ai-back-to-list-btn"
            >
              <ArrowLeft size={16} className="mr-1.5" />
              Back
            </Button>

            <div className="flex items-center gap-3 mb-8">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Sparkles size={22} className="text-white" />
              </div>
              <div>
                <h1 className="font-heading font-extrabold text-2xl text-slate-900">
                  {editId ? "Edit AI Campaign" : "New AI Outreach Campaign"}
                </h1>
                <p className="text-slate-500 text-sm">
                  {editId
                    ? "Update the brief, signature, follow-ups and schedule"
                    : "The AI writes a unique email for every prospect and handles follow-ups"}
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Basics */}
              <section className="bg-white border border-slate-200 rounded-lg p-5">
                <h2 className="font-heading font-semibold text-slate-900 mb-4">1. Campaign basics</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Campaign name</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setField("name", e.target.value)}
                      placeholder="Q3 SaaS Outreach"
                      className="mt-1"
                      data-testid="ai-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">From name (signature)</Label>
                    <Input
                      value={form.from_name}
                      onChange={(e) => setField("from_name", e.target.value)}
                      placeholder="Alex from RouteMail"
                      className="mt-1"
                      data-testid="ai-fromname-input"
                    />
                  </div>
                </div>
              </section>

              {/* Prospects */}
              <section className="bg-white border border-slate-200 rounded-lg p-5">
                <h2 className="font-heading font-semibold text-slate-900 mb-1">2. Upload prospects</h2>
                {editId ? (
                  <div className="flex items-center gap-2 text-sm text-slate-600 mt-2">
                    <Users size={16} className="text-violet-500" />
                    {editContactCount} prospects enrolled
                    <span className="text-slate-400">— the prospect list can't be changed after launch</span>
                  </div>
                ) : (
                  <>
                    <p className="text-slate-500 text-sm mb-4">
                      CSV with an <code className="text-violet-600">email</code> column. Include first_name,
                      company, title and any extra columns — the AI uses them all to personalise.
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      className="hidden"
                      data-testid="ai-prospect-file-input"
                      onChange={(e) => {
                        handleFile(e.target.files?.[0]);
                        e.target.value = "";
                      }}
                    />
                    <div className="flex items-center gap-3">
                      <Button
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="ai-upload-btn"
                      >
                        <Upload size={16} className="mr-2" />
                        {fileName ? "Replace file" : "Upload CSV"}
                      </Button>
                      {prospects.length > 0 && (
                        <span className="flex items-center gap-1.5 text-sm text-emerald-700" data-testid="ai-prospect-count">
                          <Users size={15} />
                          {prospects.length} prospects — {fileName}
                        </span>
                      )}
                    </div>
                    {columns.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {columns.map((col) => (
                          <span key={col} className="px-2 py-0.5 rounded bg-violet-50 text-violet-700 text-xs">
                            {col}
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </section>

              {/* Accounts */}
              <section className="bg-white border border-slate-200 rounded-lg p-5">
                <h2 className="font-heading font-semibold text-slate-900 mb-1">3. Sending accounts</h2>
                <p className="text-slate-500 text-sm mb-4">
                  Emails rotate across these connected accounts, respecting each one's daily limit.
                </p>
                <AccountMultiSelect
                  accounts={accounts}
                  value={form.account_ids}
                  onChange={(v) => setField("account_ids", v)}
                  testIdPrefix="ai-account-select"
                />
              </section>

              {/* Brief */}
              <section className="bg-white border border-slate-200 rounded-lg p-5">
                <h2 className="font-heading font-semibold text-slate-900 mb-4">4. Campaign brief</h2>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm">Product / service *</Label>
                    <Textarea
                      value={form.product_service}
                      onChange={(e) => setField("product_service", e.target.value)}
                      placeholder="What are you selling? Who is it for and what problem does it solve?"
                      className="mt-1 min-h-[80px]"
                      data-testid="ai-product-input"
                    />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm">Offer</Label>
                      <Input
                        value={form.offer}
                        onChange={(e) => setField("offer", e.target.value)}
                        placeholder="14-day free trial, 20% off, free audit…"
                        className="mt-1"
                        data-testid="ai-offer-input"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Call to action</Label>
                      <Input
                        value={form.cta}
                        onChange={(e) => setField("cta", e.target.value)}
                        placeholder="Book a 15-min demo"
                        className="mt-1"
                        data-testid="ai-cta-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-sm">Links to include</Label>
                    <Input
                      value={form.links}
                      onChange={(e) => setField("links", e.target.value)}
                      placeholder="https://yoursite.com/demo"
                      className="mt-1"
                      data-testid="ai-links-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Tone</Label>
                    <Select value={form.tone} onValueChange={(v) => setField("tone", v)}>
                      <SelectTrigger className="mt-1" data-testid="ai-tone-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TONES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {t}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Extra instructions</Label>
                    <Textarea
                      value={form.instructions}
                      onChange={(e) => setField("instructions", e.target.value)}
                      placeholder="Any specific angle, things to mention or avoid, sign-off style…"
                      className="mt-1 min-h-[70px]"
                      data-testid="ai-instructions-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Email signature</Label>
                    <Textarea
                      value={form.signature}
                      onChange={(e) => setField("signature", e.target.value)}
                      placeholder={"Alex Morgan\nHead of Growth, RouteMail\nhttps://routemail.co"}
                      className="mt-1 min-h-[70px] font-mono text-sm"
                      data-testid="ai-signature-input"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Added to the end of every email. Plain text (line breaks kept) or simple HTML.
                      The AI won't add its own sign-off, so no duplication.
                    </p>
                  </div>
                </div>
              </section>

              {/* Follow-ups & schedule */}
              <section className="bg-white border border-slate-200 rounded-lg p-5">
                <h2 className="font-heading font-semibold text-slate-900 mb-1">5. Follow-ups & schedule</h2>
                <p className="text-slate-500 text-sm mb-4">
                  Follow-ups are sent on the same thread and stop automatically the moment a prospect replies.
                </p>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-sm">Number of follow-ups</Label>
                    <Input
                      type="number"
                      min={0}
                      max={10}
                      value={form.num_followups}
                      onChange={(e) => setField("num_followups", e.target.value)}
                      className="mt-1"
                      data-testid="ai-followups-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Days between each email</Label>
                    <Input
                      type="number"
                      min={1}
                      max={60}
                      value={form.followup_delay_days}
                      onChange={(e) => setField("followup_delay_days", e.target.value)}
                      className="mt-1"
                      data-testid="ai-delay-input"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-sm">Time zone</Label>
                    <TimezoneSelect
                      value={form.timezone}
                      onChange={(v) => setField("timezone", v)}
                      testId="ai-timezone-select"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Start date (optional)</Label>
                    <Input
                      type="date"
                      value={form.start_date}
                      onChange={(e) => setField("start_date", e.target.value)}
                      className="mt-1"
                      data-testid="ai-startdate-input"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-sm">Sending window start</Label>
                    <Input
                      type="time"
                      value={form.start_time}
                      onChange={(e) => setField("start_time", e.target.value)}
                      className="mt-1"
                      data-testid="ai-starttime-input"
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Sending window end</Label>
                    <Input
                      type="time"
                      value={form.end_time}
                      onChange={(e) => setField("end_time", e.target.value)}
                      className="mt-1"
                      data-testid="ai-endtime-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-sm mb-2 block">Sending days</Label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS.map((d) => (
                      <button
                        key={d.v}
                        onClick={() => toggleDay(d.v)}
                        data-testid={`ai-day-${d.v}`}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                          form.sending_days.includes(d.v)
                            ? "bg-violet-600 text-white border-violet-600"
                            : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                        }`}
                      >
                        {d.l}
                      </button>
                    ))}
                  </div>
                </div>
              </section>

              <div className="flex items-center justify-end gap-3 pb-8">
                <Button variant="outline" onClick={() => { setEditId(null); setView("list"); }} data-testid="ai-cancel-btn">
                  Cancel
                </Button>
                <Button
                  onClick={handleCreate}
                  disabled={!canSubmit || submitting}
                  className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white"
                  data-testid="ai-launch-btn"
                >
                  <Wand2 size={16} className="mr-2" />
                  {submitting ? "Saving…" : editId ? "Save Changes" : "Launch AI Campaign"}
                </Button>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // ─────────────────────────── LIST VIEW ───────────────────────────
  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} setUser={setUser} />
      <main className="flex-1 p-6 lg:p-8 overflow-y-auto">
        <div className="max-w-5xl mx-auto">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/dashboard")}
            className="text-slate-500 hover:text-slate-700 -ml-2 mb-4"
            data-testid="ai-back-dashboard-btn"
          >
            <ArrowLeft size={16} className="mr-1.5" />
            Back to Dashboard
          </Button>

          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
                <Sparkles size={22} className="text-white" />
              </div>
              <div>
                <h1 className="font-heading font-extrabold text-2xl sm:text-3xl text-slate-900">
                  AI Sales Outreach
                </h1>
                <p className="text-slate-500 mt-0.5 text-sm">
                  Personalised emails + smart follow-ups, written by AI for every prospect
                </p>
              </div>
            </div>
            <Button
              onClick={startCreate}
              className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white"
              data-testid="ai-new-campaign-btn"
            >
              <Plus size={16} className="mr-2" />
              New Campaign
            </Button>
          </div>

          {campaigns.length > 0 ? (
            <div className="space-y-3">
              {campaigns.map((c, i) => (
                <motion.div
                  key={c.drip_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  className="bg-white border border-slate-200 rounded-lg p-5"
                  data-testid={`ai-campaign-${c.drip_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3">
                        <p className="font-semibold text-slate-900 truncate">{c.name}</p>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadge(c.status)}`}>
                          {c.status}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-slate-500">
                        <span className="flex items-center gap-1.5">
                          <Users size={14} /> {c.stats?.total_contacts || 0} prospects
                        </span>
                        <span className="flex items-center gap-1.5 text-blue-600">
                          <Send size={14} /> {c.stats?.emails_sent || 0} sent
                        </span>
                        <span className="flex items-center gap-1.5 text-emerald-600">
                          <Reply size={14} /> {c.stats?.replied || 0} replied
                        </span>
                        <span className="flex items-center gap-1.5">
                          <Clock size={14} /> {(c.steps?.length || 1) - 1} follow-ups
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openLogs(c)}
                        data-testid={`ai-logs-${c.drip_id}`}
                      >
                        <Eye size={15} className="mr-1" /> Logs
                      </Button>
                      {(c.status === "draft" || c.status === "paused") && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => startEdit(c)}
                          data-testid={`ai-edit-${c.drip_id}`}
                        >
                          <Edit size={15} className="mr-1" /> Edit
                        </Button>
                      )}
                      {(c.status === "running" || c.status === "paused" || c.status === "draft") && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePauseResume(c)}
                          data-testid={`ai-toggle-${c.drip_id}`}
                        >
                          {c.status === "running" ? (
                            <>
                              <Pause size={15} className="mr-1" /> Pause
                            </>
                          ) : (
                            <>
                              <Play size={15} className="mr-1" /> Start
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteId(c.drip_id)}
                        data-testid={`ai-delete-${c.drip_id}`}
                      >
                        <Trash2 size={17} className="text-slate-400 hover:text-red-500" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg p-14 text-center">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-100 to-fuchsia-100 flex items-center justify-center mx-auto mb-4">
                <Sparkles size={26} className="text-violet-500" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-slate-900 mb-1">
                No AI campaigns yet
              </h3>
              <p className="text-slate-500 mb-5 max-w-md mx-auto text-sm">
                Upload your prospect list, write a short brief, and let the AI craft a unique email for
                each person — plus follow-ups that stop the moment they reply.
              </p>
              <Button
                onClick={startCreate}
                className="bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white"
                data-testid="ai-empty-create-btn"
              >
                <Plus size={16} className="mr-2" />
                Create your first campaign
              </Button>
            </div>
          )}
        </div>
      </main>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete AI campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the campaign, its prospects and logs. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ai-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="ai-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
